<script>
import { mapGetters } from 'vuex';
import Translatable from '@src/mixins/translatable.js';
import Resizable from '@src/mixins/resizable.js';
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js';

export default {
  mixins: [Translatable, Resizable, SanitizerMixin],
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('translations', ['currentLanguage']),

    articleLink() {
      const slugs = this.article.slugs || this.article.urlSlug || {};

      const targetSlug =
        slugs[this.currentLanguage] || slugs.en || this.article.slug || this.article.url;

      return this.$l('news_detail', { slug: targetSlug });
    },

    mainImageObj() {
      const imagesArr =
        this.article.images && this.article.images.length
          ? this.article.images
          : this.article.webimages || [];

      if (!imagesArr.length) return null;

      const img = imagesArr[0];
      if (typeof img === 'object' && img.url) return img;
      return { url: img, alt: {}, title: {} };
    },

    backgroundImage() {
      const url = this.mainImageObj ? this.mainImageObj.url : '';
      return {
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover',
      };
    },
    truncationLimit() {
      return this.desktopScreen ? 620 : 210;
    },
  },
  methods: {
    getSeoText(obj) {
      if (!obj) return '';
      return obj[this.currentLanguage] || obj.en || '';
    },
  },
};
</script>

<template>
  <div class="card-container">
    <div
      class="article-image"
      :style="backgroundImage"
      :title="getSeoText(mainImageObj ? mainImageObj.title : {})"
      role="img"
      :aria-label="getSeoText(mainImageObj ? mainImageObj.alt : {})"
    >
      <div class="article-date">
        {{ article.datePublished | ddmmYYYY }}
      </div>
    </div>

    <div class="article-content">
      <router-link class="link" :to="articleLink">
        <h4 class="article-title">
          {{ getTranslatedContent('title', article.content) }}
        </h4>
      </router-link>

      <div
        class="article-text"
        :inner-html.prop="description(article.content) | truncate(truncationLimit)"
      ></div>

      <router-link class="link" :to="articleLink">
        <p class="read-more">
          {{ getTranslatedContent('READ_MORE') }}
        </p>
      </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.card-container {
  display: grid;
  column-gap: 20px;
  width: 100%;
  height: auto;
  grid-template-columns: repeat(2, 1fr);
}

.card-container:nth-child(2n) {
  .article-content {
    order: 1;
  }
  .article-image {
    order: 2;
  }
}

.article-image {
  background: $dark-gray;
  display: flex;
  height: 350px;
  justify-content: flex-end;
  align-items: flex-end;
  border-radius: 10px;
  border: none;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.article-date {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 90px;
  height: 27px;
  background: $gray;
  border-radius: 10px 0;

  font-family: Open Sans Semibold, sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 14px;
  line-height: 18px;
  color: $white;
}

.article-content {
  min-height: 350px;
  height: auto;
}

h4.article-title {
  font-family: Open Sans Bold, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 23px;
  // margin-top: 24px;
  text-transform: uppercase;
  color: $dark-blue;

  &:hover {
    color: $gray;
  }
}

::v-deep .article-text {
  margin-top: 15px;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  // color: rgb(37, 37, 39, 0.8);
  color: $dark-blue;
  user-select: none;
  & p:empty {
    height: 15px;
    margin: 0;
  }

  & p:not(:last-child):not(:empty) {
    margin-bottom: 15px;
  }

  & p > br {
    display: none;
  }
  & p:has(br:last-child)::after {
    content: '';
    display: block;
    height: 15px;
  }

  & em {
    font-style: italic;
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & .ql-align-center {
    text-align: center;
  }
}

.link {
  text-decoration: none;
}

.read-more {
  cursor: pointer;
  margin-top: 25px;
  text-decoration: underline;
  text-transform: capitalize;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: $orange;
}

.read-more:hover {
  text-decoration: none;
  color: $hover-orange;
}

@media only screen and (min-width: $mobile-screen-width) and (max-width: $container-width-xs) {
  .card-container {
    grid-template-columns: repeat(1, 1fr);
  }

  .card-container:nth-child(1n) {
    .article-content {
      order: 2;
    }
    .article-image {
      order: 1;
    }
  }

  .article-image {
    border-radius: 0;
    height: 261px;
  }

  .article-date {
    border-radius: 10px 0 0 0;
  }

  .article-content {
    padding: 0 8px;
    min-height: auto;
  }

  h4.article-title {
    font-size: 16px;
    line-height: 21px;
    margin-top: 22px;
  }
}
</style>
